import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { Link } from 'react-router-dom'
import {
  Key,
  Clock,
  Activity,
  AlertTriangle,
  DollarSign,
  ExternalLink,
  Shield,
  Server,
  Eye,
  Brain,
  Settings,
  HardDrive,
  AlertCircle,
  CheckCircle,
  XCircle,
  Zap,
  Monitor,
  Gauge
} from 'lucide-react'

interface Stats {
  totalLicenses: number
  activeLicenses: number
  pendingTokens: number
}

interface SystemAlert {
  id: number
  alertType: string
  severity: 'info' | 'warning' | 'critical'
  message: string
  details?: {
    remaining?: number
    totalCredits?: number
    totalUsage?: number
    actionRequired?: string
    timestamp?: string
  }
  isActive: boolean
  isAcknowledged: boolean
  createdAt: string
}

interface OpenRouterCredits {
  totalCredits: number
  usage: number
  remaining: number
  status: 'ok' | 'low' | 'critical' | 'exhausted'
}

interface ProveEyeStatus {
  online: boolean
  message?: string
}

interface ScrapflyCredits {
  remaining: number
  limit: number
  used: number
  plan: string
  status: 'ok' | 'low' | 'critical' | 'exhausted'
}

export default function DashboardPage() {
  const queryClient = useQueryClient()

  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const token = localStorage.getItem('admin-token')
      const response = await axios.get('/api/admin/stats', {
        headers: { Authorization: `Bearer ${token}` }
      })
      return response.data as Stats
    }
  })

  // Fetch system alerts
  const { data: alertsData } = useQuery({
    queryKey: ['system-alerts'],
    queryFn: async () => {
      const token = localStorage.getItem('admin-token')
      const response = await axios.get('/api/admin/system-alerts', {
        headers: { Authorization: `Bearer ${token}` }
      })
      return response.data as { success: boolean; alerts: SystemAlert[] }
    },
    refetchInterval: 30000,
    retry: false
  })

  // Fetch OpenRouter credits
  const { data: creditsData } = useQuery({
    queryKey: ['openrouter-credits'],
    queryFn: async () => {
      const token = localStorage.getItem('admin-token')
      const response = await axios.get('/api/admin/openrouter-credits', {
        headers: { Authorization: `Bearer ${token}` }
      })
      return response.data as { success: boolean; credits: OpenRouterCredits | null; error?: string }
    },
    refetchInterval: 60000,
    retry: false
  })

  // Fetch ProveEye status
  const { data: proveEyeStatusData } = useQuery({
    queryKey: ['proveye-status'],
    queryFn: async () => {
      try {
        const response = await axios.get('/admin/api/proveye/status')
        return { online: response.data?.success === true, message: response.data?.message } as ProveEyeStatus
      } catch (error) {
        return { online: false, message: 'Connection failed' } as ProveEyeStatus
      }
    },
    refetchInterval: 30000
  })

  // Fetch Scrapfly credits
  const { data: scrapflyCreditsData } = useQuery({
    queryKey: ['scrapfly-credits'],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('admin-token')
        const response = await axios.get('/api/admin/scrapfly-credits', {
          headers: { Authorization: `Bearer ${token}` }
        })
        return response.data as { success: boolean; credits: ScrapflyCredits | null; error?: string }
      } catch (error) {
        return { success: false, credits: null, error: 'Connection failed' }
      }
    },
    refetchInterval: 60000,
    retry: false
  })

  const proveEyeOnline = proveEyeStatusData?.online ?? false

  const systemAlerts = alertsData?.alerts || []
  const credits = creditsData?.credits
  const isCreditLow = credits?.status === 'low' || credits?.status === 'critical' || credits?.status === 'exhausted'

  // Scrapfly credits from API
  const scrapflyCredits = scrapflyCreditsData?.credits || { remaining: 0, limit: 0, used: 0, plan: 'Unknown', status: 'exhausted' as const }
  const licensingAlerts = stats?.pendingTokens || 0
  const proveEyeAlerts = 2 // Replace with real data
  const healthIssues = credits?.status === 'exhausted' ? 1 : 0

  // Control cards configuration
  // Layout: Col1 (DigitalOcean/Coolify), Col2 (Licenses/Plans), Col3 (ProvEye Scans/AI Models)
  const controlCards = [
    // Row 1
    {
      title: 'DigitalOcean',
      description: 'Customer instances & backup storage',
      icon: Server,
      href: '/demo-instances',
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      iconBg: 'bg-blue-500',
      textColor: 'text-blue-700',
      status: 'operational',
      count: 12
    },
    {
      title: 'Licenses',
      description: 'License management & tokens',
      icon: Key,
      href: '/licenses',
      color: 'cyan',
      bgColor: 'bg-cyan-50',
      borderColor: 'border-cyan-200',
      iconBg: 'bg-cyan-500',
      textColor: 'text-cyan-700',
      status: licensingAlerts > 0 ? 'warning' : 'operational',
      count: stats?.totalLicenses || 0
    },
    {
      title: 'ProvEye Scans',
      description: 'Security intelligence & monitoring',
      icon: Eye,
      href: '/proveye',
      color: 'orange',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      iconBg: 'bg-orange-500',
      textColor: 'text-orange-700',
      status: !proveEyeOnline ? 'critical' : proveEyeAlerts > 0 ? 'warning' : 'operational',
      count: proveEyeAlerts,
      showOnlineStatus: true,
      isOnline: proveEyeOnline
    },
    // Row 2
    {
      title: 'Coolify',
      description: 'Demo instances management',
      icon: Monitor,
      href: '/demo-instances',
      color: 'purple',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      iconBg: 'bg-purple-500',
      textColor: 'text-purple-700',
      status: 'operational',
      count: 5,
      subLink: {
        title: 'Server Monitor',
        href: 'http://100.65.151.125:3333',
        icon: Gauge,
        external: true
      }
    },
    {
      title: 'Plans',
      description: 'Subscription plans & pricing',
      icon: Settings,
      href: '/plans',
      color: 'slate',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-200',
      iconBg: 'bg-slate-600',
      textColor: 'text-slate-700',
      status: 'operational',
      count: null
    },
    {
      title: 'AI Models',
      description: 'Model configuration & usage',
      icon: Brain,
      href: '/ai-models',
      color: 'emerald',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      iconBg: 'bg-emerald-500',
      textColor: 'text-emerald-700',
      status: credits?.status === 'exhausted' ? 'critical' : credits?.status === 'low' ? 'warning' : 'operational',
      count: null
    }
  ]

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircle className="h-4 w-4 text-emerald-500" />
      case 'warning':
        return <AlertCircle className="h-4 w-4 text-amber-500" />
      case 'critical':
        return <XCircle className="h-4 w-4 text-red-500" />
      default:
        return <CheckCircle className="h-4 w-4 text-emerald-500" />
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border-2 border-emerald-500/30 border-t-emerald-500 animate-spin" />
          <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-mono)' }}>
            Loading dashboard...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Info Ribbon - Full Width */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="grid grid-cols-5 divide-x divide-slate-200">
          {/* OpenRouter Credits */}
          <a
            href="https://openrouter.ai/settings/credits"
            target="_blank"
            rel="noopener noreferrer"
            className={`px-3 py-2.5 flex items-center gap-2.5 hover:bg-slate-50 transition-colors ${
              credits?.status === 'exhausted' ? 'bg-red-50' :
              credits?.status === 'critical' ? 'bg-red-50/50' :
              credits?.status === 'low' ? 'bg-amber-50/50' : ''
            }`}
          >
            <div className={`p-1.5 rounded-md ${
              credits?.status === 'exhausted' ? 'bg-red-500' :
              credits?.status === 'critical' ? 'bg-red-400' :
              credits?.status === 'low' ? 'bg-amber-500' :
              'bg-emerald-500'
            }`}>
              <DollarSign className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">OpenRouter</p>
              <p className={`text-base font-bold ${
                credits?.status === 'exhausted' ? 'text-red-600' :
                credits?.status === 'critical' ? 'text-red-600' :
                credits?.status === 'low' ? 'text-amber-600' :
                'text-slate-800'
              }`} style={{ fontFamily: 'var(--font-mono)' }}>
                ${credits?.remaining?.toFixed(2) || '0.00'}
              </p>
            </div>
            <ExternalLink className="h-3 w-3 text-slate-400" />
          </a>

          {/* Scrapfly Credits */}
          <a
            href="https://scrapfly.io/dashboard"
            target="_blank"
            rel="noopener noreferrer"
            className={`px-3 py-2.5 flex items-center gap-2.5 hover:bg-slate-50 transition-colors ${
              scrapflyCredits?.status === 'exhausted' ? 'bg-red-50' :
              scrapflyCredits?.status === 'critical' ? 'bg-red-50/50' :
              scrapflyCredits?.status === 'low' ? 'bg-amber-50/50' : ''
            }`}
          >
            <div className={`p-1.5 rounded-md ${
              scrapflyCredits?.status === 'exhausted' ? 'bg-red-500' :
              scrapflyCredits?.status === 'critical' ? 'bg-red-400' :
              scrapflyCredits?.status === 'low' ? 'bg-amber-500' :
              'bg-blue-500'
            }`}>
              <Zap className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Scrapfly</p>
              <p className={`text-base font-bold ${
                scrapflyCredits?.status === 'exhausted' ? 'text-red-600' :
                scrapflyCredits?.status === 'critical' ? 'text-red-600' :
                scrapflyCredits?.status === 'low' ? 'text-amber-600' :
                'text-slate-800'
              }`} style={{ fontFamily: 'var(--font-mono)' }}>
                {scrapflyCredits?.remaining || 0} <span className="text-xs font-normal text-slate-400">/ {scrapflyCredits?.limit || 0}</span>
              </p>
            </div>
            <ExternalLink className="h-3 w-3 text-slate-400" />
          </a>

          {/* Licensing Alerts */}
          <Link
            to="/licenses"
            className={`px-3 py-2.5 flex items-center gap-2.5 hover:bg-slate-50 transition-colors ${
              licensingAlerts > 0 ? 'bg-amber-50/50' : ''
            }`}
          >
            <div className={`p-1.5 rounded-md ${licensingAlerts > 0 ? 'bg-amber-500' : 'bg-emerald-500'}`}>
              <Key className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">License Alerts</p>
              <p className={`text-base font-bold ${licensingAlerts > 0 ? 'text-amber-600' : 'text-slate-800'}`} style={{ fontFamily: 'var(--font-mono)' }}>
                {licensingAlerts} pending
              </p>
            </div>
          </Link>

          {/* ProveEye Scan Alerts */}
          <Link
            to="/proveye"
            className={`px-3 py-2.5 flex items-center gap-2.5 hover:bg-slate-50 transition-colors ${
              proveEyeAlerts > 0 ? 'bg-orange-50/50' : ''
            }`}
          >
            <div className={`p-1.5 rounded-md ${proveEyeAlerts > 0 ? 'bg-orange-500' : 'bg-emerald-500'}`}>
              <Eye className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">ProveEye Alerts</p>
              <p className={`text-base font-bold ${proveEyeAlerts > 0 ? 'text-orange-600' : 'text-slate-800'}`} style={{ fontFamily: 'var(--font-mono)' }}>
                {proveEyeAlerts} requests
              </p>
            </div>
          </Link>

          {/* Health Issues */}
          <Link
            to="/system-health"
            className={`px-3 py-2.5 flex items-center gap-2.5 hover:bg-slate-50 transition-colors ${
              healthIssues > 0 ? 'bg-red-50' : ''
            }`}
          >
            <div className={`p-1.5 rounded-md ${healthIssues > 0 ? 'bg-red-500' : 'bg-emerald-500'}`}>
              <Activity className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Health Issues</p>
              <p className={`text-base font-bold ${healthIssues > 0 ? 'text-red-600' : 'text-emerald-600'}`} style={{ fontFamily: 'var(--font-mono)' }}>
                {healthIssues > 0 ? `${healthIssues} issue${healthIssues > 1 ? 's' : ''}` : 'All Good'}
              </p>
            </div>
          </Link>
        </div>
      </div>

      {/* Large Control Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {controlCards.map((card) => {
          const Icon = card.icon
          const showOnlineStatus = 'showOnlineStatus' in card && card.showOnlineStatus
          const isOnline = 'isOnline' in card ? card.isOnline : true
          return (
            <Link
              key={card.title}
              to={card.href}
              className={`group relative p-4 rounded-xl border-2 ${card.borderColor} ${card.bgColor} hover:shadow-lg transition-all duration-300 hover:scale-[1.01]`}
            >
              {/* Status indicator */}
              <div className="absolute top-3 right-3 flex items-center gap-1">
                {showOnlineStatus ? (
                  <>
                    <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
                    <span className={`text-[10px] font-medium ${isOnline ? 'text-emerald-600' : 'text-red-600'}`}>
                      {isOnline ? 'Online' : 'Offline'}
                    </span>
                  </>
                ) : (
                  <>
                    {getStatusIcon(card.status)}
                    <span className={`text-[10px] font-medium ${
                      card.status === 'operational' ? 'text-emerald-600' :
                      card.status === 'warning' ? 'text-amber-600' :
                      'text-red-600'
                    }`}>
                      {card.status === 'operational' ? 'OK' : card.status === 'warning' ? 'Warning' : 'Critical'}
                    </span>
                  </>
                )}
              </div>

              {/* Icon */}
              <div className={`w-12 h-12 rounded-xl ${card.iconBg} flex items-center justify-center mb-3 shadow-md group-hover:scale-105 transition-transform duration-300 relative`}>
                <Icon className="h-6 w-6 text-white" />
                {/* Online status light inside icon badge for ProveEye */}
                {showOnlineStatus && (
                  <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white ${isOnline ? 'bg-emerald-500' : 'bg-red-500'} flex items-center justify-center`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-white animate-pulse' : 'bg-red-200'}`} />
                  </div>
                )}
              </div>

              {/* Content */}
              <h3 className={`text-base font-bold ${card.textColor} mb-0.5`}>
                {card.title}
              </h3>
              <p className="text-xs text-slate-500 mb-2.5">
                {card.description}
              </p>

              {/* Count badge */}
              {card.count !== null && (
                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${card.bgColor} border ${card.borderColor}`}>
                  <span className={`text-xs font-bold ${card.textColor}`} style={{ fontFamily: 'var(--font-mono)' }}>
                    {card.count}
                  </span>
                  <span className="text-[10px] text-slate-500">
                    {card.title === 'Licenses' ? 'total' :
                     card.title === 'ProvEye Scans' ? 'alerts' :
                     card.title === 'DigitalOcean' ? 'instances' :
                     card.title === 'Coolify' ? 'demos' : 'items'}
                  </span>
                </div>
              )}

              {/* Sub-link badge */}
              {'subLink' in card && card.subLink && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    window.open(card.subLink!.href, '_blank', 'noopener,noreferrer')
                  }}
                  className={`mt-2 inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-white/80 border ${card.borderColor} hover:bg-white hover:shadow-md transition-all duration-200`}
                >
                  <Gauge className={`h-3.5 w-3.5 ${card.textColor}`} />
                  <span className={`text-xs font-medium ${card.textColor}`}>
                    {card.subLink.title}
                  </span>
                  <ExternalLink className="h-2.5 w-2.5 text-slate-400" />
                </button>
              )}

              {/* Hover arrow */}
              <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className={`w-6 h-6 rounded-full ${card.iconBg} flex items-center justify-center`}>
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          )
        })}
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-3 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-100">
            <Key className="h-4 w-4 text-emerald-600" />
          </div>
          <div>
            <p className="text-lg font-bold text-slate-800" style={{ fontFamily: 'var(--font-mono)' }}>
              {stats?.activeLicenses || 0}
            </p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Active Licenses</p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-3 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-cyan-100">
            <Server className="h-4 w-4 text-cyan-600" />
          </div>
          <div>
            <p className="text-lg font-bold text-slate-800" style={{ fontFamily: 'var(--font-mono)' }}>
              12
            </p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Active Instances</p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-3 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-100">
            <Eye className="h-4 w-4 text-purple-600" />
          </div>
          <div>
            <p className="text-lg font-bold text-slate-800" style={{ fontFamily: 'var(--font-mono)' }}>
              847
            </p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">ProveEye Scans</p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-3 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-100">
            <Clock className="h-4 w-4 text-amber-600" />
          </div>
          <div>
            <p className="text-lg font-bold text-slate-800" style={{ fontFamily: 'var(--font-mono)' }}>
              {stats?.pendingTokens || 0}
            </p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Pending Tokens</p>
          </div>
        </div>
      </div>
    </div>
  )
}
